import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  extractHashtags,
  extractMentions,
  extractUrls
} from 'components/inputs/EditorInput/strategies';
import FieldError from 'components/inputs/FieldError/Styled';

export interface ContentValidatorProps {
  originalContent: string;
  content: string;
}

const validateHashtags = (
  newContent: string,
  originalContent: string
): [boolean, string[]] => {
  const newHashtags = extractHashtags(newContent);
  const oldHashtags = extractHashtags(originalContent);
  const missingHashtags = oldHashtags.filter(tag => !newHashtags.includes(tag));
  return [missingHashtags.length === 0, missingHashtags];
};

const validateMentions = (
  newContent: string,
  originalContent: string
): [boolean, string[]] => {
  const newMentions = extractMentions(newContent);
  const oldMentions = extractMentions(originalContent);
  const missingMentions = oldMentions.filter(
    mention => !newMentions.includes(mention)
  );
  return [missingMentions.length === 0, missingMentions];
};

const validateUrls = (
  newContent: string,
  originalContent: string
): [boolean, string[]] => {
  const newUrls = extractUrls(newContent);
  const oldUrls = extractUrls(originalContent);
  const missingUrls = oldUrls.filter(url => !newUrls.includes(url));
  return [missingUrls.length === 0, missingUrls];
};

const ContentValidator: React.FC<ContentValidatorProps> = ({
  originalContent,
  content
}) => {
  const { t } = useTranslation(['content_editor']);

  const [hashtagsValid, missingHashtags] = validateHashtags(
    content,
    originalContent
  );
  const [mentionsValid, missingMentions] = validateMentions(
    content,
    originalContent
  );
  const [urlsValid, missingUrls] = validateUrls(content, originalContent);

  const errors = [];
  if (!hashtagsValid) {
    errors.push(
      t('missing_hashtags', { hashtags: missingHashtags.join(', ') })
    );
  }
  if (!mentionsValid) {
    errors.push(
      t('missing_mentions', { mentions: missingMentions.join(', ') })
    );
  }
  if (!urlsValid) {
    errors.push(t('missing_urls', { urls: missingUrls.join(', ') }));
  }

  if (errors.length === 0) return null;

  return (
    <div>
      {errors.map((error, index) => (
        <FieldError key={index}>{error}</FieldError>
      ))}
    </div>
  );
};

export default ContentValidator;
